import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Cliente, Pedido, StatusPedido, RegistroAuditoria, EtapaOps, FaturamentoPeriodo } from '@pgcustom/shared-types';
import { ETAPAS_OPS, ETAPA_CONCLUSAO } from '@pgcustom/shared-types';
import type { PedidosApi } from '../pedidos/pedidos.api.js';
import type { FinanceiroApi } from '../financeiro/financeiro.api.js';
import type { OpsApi } from '../producao/ops.api.js';
import type { EstoqueApi } from '../estoque/estoque.api.js';
import type { SuporteApi } from '../suporte/suporte.api.js';

/** Resumo real de estoque para o bloco do Dashboard — agregado no frontend sobre `GET /estoque/insumos` (a API não expõe uma contagem própria). */
export interface ResumoEstoque {
  totalInsumosAtivos: number;
  abaixoDoPontoDeReposicao: number;
}

/** Valor monetário (centavos) já pronto para exibição em KPI de moeda — mesma limitação de `Contagem`: sem contagem/soma total exposta pela API, é derivado da página buscada. */
export interface ValorMonetario {
  centavos: number;
  aproximado: boolean;
}

/** Um item real de "precisa de atenção agora" — nunca um texto livre inventado, sempre derivado de uma contagem real sobre dado existente. */
export interface ItemAviso {
  titulo: string;
  quantidade: number;
}

/**
 * Contagem aproximada por página cheia — mesmo formato para qualquer
 * recurso cuja API de listagem não expõe contagem total própria
 * (`clientes`/`pedidos`, ambos com `limit` máximo de 100).
 */
export interface Contagem {
  total: number;
  /** `true` quando o total foi capado pelo limite de página da listagem. */
  aproximado: boolean;
  /**
   * Série curta (sparkline) derivada dos MESMOS registros já buscados para calcular `total` —
   * nunca uma chamada nova, nunca um número inventado (`criadoEm` de cada item, agrupado em até 6
   * baldes cronológicos). Omitido quando há menos de 2 itens (nada a desenhar).
   */
  pontos?: number[];
}

/**
 * Adapter de dado do Dashboard. Cada método documenta explicitamente se
 * o dado é real (chama um endpoint que existe) ou `indisponivel` (o
 * módulo/endpoint correspondente ainda não existe no backend) — regra
 * de produto: nunca inventar número para preencher um card.
 */
export class DashboardApi {
  constructor(
    private readonly api: ApiClient,
    private readonly pedidosApi: PedidosApi,
    private readonly financeiroApi: FinanceiroApi,
    private readonly opsApi: OpsApi,
    private readonly estoqueApi: EstoqueApi,
    private readonly suporteApi: SuporteApi
  ) {}

  /**
   * Total de clientes ativos. REAL: usa `GET /api/v1/clientes`
   * (`apps/api/src/modules/clientes`), que já existe. A API não expõe
   * uma contagem própria (nenhum campo `total` na resposta de listagem
   * — `clientes.controller.ts`), então o valor é o tamanho da página
   * retornada (limite máximo permitido, 100 — `clientes.schema.ts`).
   * Se a página vier cheia, `aproximado: true` sinaliza que o número
   * real pode ser maior — nunca é exibido como total exato nesse caso.
   */
  async contarClientes(): Promise<Resultado<Contagem>> {
    try {
      // `incluirExcluidos` não é aceito por `listarClientesQuerySchema`
      // (`apps/api/src/modules/clientes/clientes.schema.ts`) — clientes
      // excluídos (soft delete) já ficam de fora por padrão no
      // repositório, sem precisar de nenhum parâmetro para isso.
      const resposta = await this.api.get<{ clientes: Cliente[] }>('/clientes', { limit: 100 });
      const total = resposta.clientes.length;
      if (total === 0) return { status: 'vazio' };
      const pontos = bucketarCumulativo(resposta.clientes, (c) => c.criadoEm);
      return {
        status: 'ok',
        dado: { total, aproximado: total === 100, ...(pontos ? { pontos } : {}) },
      };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar clientes' };
    }
  }

  /**
   * Ticket médio: média de `valorTotalCentavos` dos pedidos com status
   * `pago` (`GET /api/v1/pedidos?status=pago`, módulo Pedidos existente
   * desde a Sprint 4) — calculado no frontend porque a API não expõe
   * agregação própria. Mesma limitação de página (100) das demais
   * contagens desta tela.
   */
  async obterTicketMedio(): Promise<Resultado<ValorMonetario>> {
    const resultado = await this.pedidosApi.listar({ status: 'pago', limit: 100 });
    if (resultado.status !== 'ok') return resultado;
    const total = resultado.dado.reduce((soma, p) => soma + p.valorTotalCentavos, 0);
    const centavos = Math.round(total / resultado.dado.length);
    return { status: 'ok', dado: { centavos, aproximado: resultado.dado.length === 100 } };
  }

  /**
   * Pedidos por status: contagem de `pendente`/`pago`/`cancelado` sobre
   * a mesma página usada por `contarPedidos` (`GET /api/v1/pedidos`,
   * limite 100) — nenhum endpoint novo, apenas agregação no frontend
   * (a API não expõe `GROUP BY status`). `aproximado` segue a mesma
   * regra: se a página veio cheia, a distribuição real pode divergir.
   */
  async obterPedidosPorStatus(): Promise<
    Resultado<{ contagem: Record<StatusPedido, number>; aproximado: boolean; pedidos: Pedido[] }>
  > {
    const resultado = await this.pedidosApi.listar({ limit: 100 });
    if (resultado.status !== 'ok') return resultado;
    const contagem: Record<StatusPedido, number> = { pendente: 0, pago: 0, cancelado: 0 };
    for (const pedido of resultado.dado) contagem[pedido.status]++;
    // `pedidos` (a lista completa, não só a contagem) vai junto porque `dashboard.page.ts` reaproveita
    // os mesmos registros para as sparklines de "Pedidos" e "Ticket médio" — nenhuma chamada nova.
    return { status: 'ok', dado: { contagem, aproximado: resultado.dado.length === 100, pedidos: resultado.dado } };
  }

  /**
   * Pedidos mais recentes: `GET /api/v1/pedidos?limit=5`, já ordenado
   * por `criado_em DESC` pelo backend (`pedidos.repository.postgres.ts`)
   * — nenhuma ordenação nova no frontend, só uma página menor da mesma
   * listagem real.
   */
  async obterPedidosRecentes(): Promise<Resultado<Pedido[]>> {
    return this.pedidosApi.listar({ limit: 5 });
  }

  /**
   * OPS ativas: contagem de OPS que ainda não chegaram em "Enviado"
   * (`ETAPA_CONCLUSAO`) — `GET /api/v1/ops` (Sprint 6, existente desde
   * esta sessão). Mesma limitação de página das demais contagens
   * (limite 100).
   */
  async obterResumoProducao(): Promise<Resultado<Contagem>> {
    const resultado = await this.opsApi.listar({ limit: 100 });
    if (resultado.status !== 'ok') return resultado;
    const opsAtivas = resultado.dado.filter((ops) => ops.etapa !== ETAPA_CONCLUSAO);
    if (opsAtivas.length === 0) return { status: 'vazio' };
    const pontos = bucketarContagem(opsAtivas, (o) => o.criadoEm);
    return {
      status: 'ok',
      dado: { total: opsAtivas.length, aproximado: resultado.dado.length === 100, ...(pontos ? { pontos } : {}) },
    };
  }

  /**
   * OPS por etapa: mesma ideia de `obterPedidosPorStatus` — agregação no
   * frontend sobre a mesma página de `GET /api/v1/ops` (a API não expõe
   * `GROUP BY etapa`).
   */
  async obterOpsPorEtapa(): Promise<Resultado<{ contagem: Record<EtapaOps, number>; aproximado: boolean }>> {
    const resultado = await this.opsApi.listar({ limit: 100 });
    if (resultado.status !== 'ok') return resultado;
    const contagem = Object.fromEntries(ETAPAS_OPS.map((etapa) => [etapa, 0])) as Record<EtapaOps, number>;
    for (const ops of resultado.dado) contagem[ops.etapa]++;
    return { status: 'ok', dado: { contagem, aproximado: resultado.dado.length === 100 } };
  }

  /**
   * REAL: `GET /api/v1/auditoria?limit=10` (Core, existente desde a
   * migration `001` da Sprint 0 — a rota HTTP em si é nova). Mesma
   * RBAC de `/core/status`: só `admin` tem `auth:ler` hoje, então
   * `sem-permissao` é o caminho real para qualquer outro papel, nunca
   * um placeholder.
   */
  async obterAtividadesRecentes(): Promise<Resultado<RegistroAuditoria[]>> {
    try {
      const resposta = await this.api.get<{ registros: RegistroAuditoria[] }>('/auditoria', { limit: 10 });
      if (resposta.registros.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.registros };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar atividades recentes',
      };
    }
  }

  /**
   * Faturamento por período (mês) — `GET /financeiro/faturamento-por-
   * periodo` (Sprint 5+, nesta sessão): agregação real no Postgres
   * (`date_trunc('month', ...)`), não no frontend — diferente de
   * `obterFaturamento`, que soma só a página capada de 100 transações.
   * `meses` alimenta o filtro de período do Dashboard — repassado como
   * está, sem transformação (a API já aceita esse parâmetro).
   */
  async obterFaturamentoPorPeriodo(meses = 6): Promise<Resultado<FaturamentoPeriodo[]>> {
    return this.financeiroApi.obterFaturamentoPorPeriodo(meses);
  }

  /**
   * Sparklines de Receita/Lucro quando `obterFaturamentoPorPeriodo` (agregado por MÊS) só tem 1
   * ponto — comum logo no início de operação, quando toda transação real ainda cai no mesmo mês.
   * Em vez de esperar um 2º mês existir (ou inventar um), agrupamos as transações individuais já
   * reais de `GET /financeiro/transacoes` (mesmo endpoint do módulo Financeiro, `listarTransacoes`
   * — nenhuma rota nova) em até 6 baldes cronológicos por `criadoEm`, com granularidade de dias em
   * vez de mês. Entrada/saída de um MESMO balde (não bucketadas em separado) garante que
   * `lucroPontos[i] = receitaPontos[i] - despesaPonto` corresponda à mesma janela de tempo.
   */
  async obterEvolucaoFinanceiraDetalhada(): Promise<Resultado<{ receitaPontos?: number[]; lucroPontos?: number[] }>> {
    const resultado = await this.financeiroApi.listarTransacoes({ limit: 100 });
    if (resultado.status !== 'ok') return resultado;
    const baldes = agruparEmBaldes(resultado.dado, (t) => t.criadoEm, 6);
    if (baldes.length < 2) return { status: 'ok', dado: {} };
    const receitaPontos = baldes.map((balde) => balde.filter((t) => t.tipo === 'receita').reduce((soma, t) => soma + t.valorCentavos, 0));
    const lucroPontos = baldes.map(
      (balde, i) => receitaPontos[i]! - balde.filter((t) => t.tipo === 'despesa').reduce((soma, t) => soma + t.valorCentavos, 0)
    );
    return { status: 'ok', dado: { receitaPontos, lucroPontos } };
  }

  /**
   * Resumo real de estoque: total de insumos ativos + quantos estão
   * abaixo do ponto de reposição — `GET /estoque/insumos` (Sprint 8),
   * mesmo dado já usado por `<erp-estoque-page>`, agregado aqui só para
   * o bloco do Dashboard (`Insumo.abaixoDoPontoDeReposicao` já vem
   * calculado pelo backend — nenhuma fórmula nova no frontend).
   */
  async obterResumoEstoque(): Promise<Resultado<ResumoEstoque>> {
    const resultado = await this.estoqueApi.listarInsumos();
    if (resultado.status === 'vazio') return { status: 'vazio' };
    if (resultado.status !== 'ok') return resultado;
    const abaixoDoPontoDeReposicao = resultado.dado.filter((i) => i.abaixoDoPontoDeReposicao).length;
    return {
      status: 'ok',
      dado: { totalInsumosAtivos: resultado.dado.length, abaixoDoPontoDeReposicao },
    };
  }

  /**
   * Avisos e pendências: REAL, mas deliberadamente reinterpretado — o
   * sistema de notificações do Core (`core/notifications`) existe desde
   * a Sprint 0, mas nenhum módulo jamais chama `NotificationService.
   * notificar()` (confirmado por busca no código nesta sessão); expor
   * essa fila mostraria sempre "vazio", nunca dado real de verdade, e
   * inventar um gatilho de notificação (ex: "pedido pendente há mais de
   * X dias") criaria uma regra de negócio com um número mágico que não
   * está documentado em nenhum lugar — exatamente o que a instrução
   * desta sessão proíbe.
   *
   * Em vez disso, "avisos" aqui é sempre a contagem real, sem nenhum
   * limiar/threshold inventado, de dois fatos operacionais já
   * existentes: pedidos aguardando pagamento (`GET /pedidos?
   * status=pendente`) e OPS sem técnico responsável ainda atribuído
   * (`GET /ops`, filtrado no frontend por `tecnicoId === null` e etapa
   * não concluída — o mesmo padrão de agregação já usado em
   * `obterPedidosPorStatus`/`obterOpsPorEtapa`). Cada fonte é buscada
   * de forma independente (`allSettled`): se uma delas falhar por RBAC
   * (papel sem `pedidos:ler` ou sem `producao:ler`), o item
   * correspondente simplesmente não aparece — nunca bloqueia o outro
   * item nem finge dado que não pôde ser lido. `sem-permissao` só é
   * devolvido quando NENHuma das duas fontes pôde ser lida.
   */
  async obterAvisos(): Promise<Resultado<ItemAviso[]>> {
    const [pedidosResultado, opsResultado, ticketsResultado] = await Promise.allSettled([
      this.pedidosApi.listar({ status: 'pendente', limit: 100 }),
      this.opsApi.listar({ limit: 100 }),
      this.suporteApi.listar({ status: 'aberto', limit: 100 }),
    ]);

    const itens: ItemAviso[] = [];
    let algumaFonteAcessivel = false;
    let algumaFonteComErroReal = false;

    if (pedidosResultado.status === 'fulfilled') {
      const resultado = pedidosResultado.value;
      if (resultado.status === 'ok') {
        algumaFonteAcessivel = true;
        itens.push({ titulo: 'Pedidos aguardando pagamento', quantidade: resultado.dado.length });
      } else if (resultado.status === 'vazio') {
        algumaFonteAcessivel = true;
      } else if (resultado.status === 'erro') {
        algumaFonteComErroReal = true;
      }
    }

    if (opsResultado.status === 'fulfilled') {
      const resultado = opsResultado.value;
      if (resultado.status === 'ok') {
        algumaFonteAcessivel = true;
        const semTecnico = resultado.dado.filter((ops) => !ops.tecnicoId && ops.etapa !== 'enviado').length;
        if (semTecnico > 0) itens.push({ titulo: 'OPS sem técnico atribuído', quantidade: semTecnico });
      } else if (resultado.status === 'vazio') {
        algumaFonteAcessivel = true;
      } else if (resultado.status === 'erro') {
        algumaFonteComErroReal = true;
      }
    }

    if (ticketsResultado.status === 'fulfilled') {
      const resultado = ticketsResultado.value;
      if (resultado.status === 'ok') {
        algumaFonteAcessivel = true;
        itens.push({ titulo: 'Solicitações de suporte abertas', quantidade: resultado.dado.length });
      } else if (resultado.status === 'vazio') {
        algumaFonteAcessivel = true;
      } else if (resultado.status === 'erro') {
        algumaFonteComErroReal = true;
      }
    }

    if (!algumaFonteAcessivel && !algumaFonteComErroReal) return { status: 'sem-permissao' };
    if (!algumaFonteAcessivel && algumaFonteComErroReal) {
      return { status: 'erro', mensagem: 'falha ao carregar avisos e pendências' };
    }
    if (itens.length === 0) return { status: 'vazio' };
    return { status: 'ok', dado: itens };
  }
}

/**
 * Agrupa itens em até `maxBaldes` grupos cronológicos (por índice após ordenar por `dataDe`, não
 * por calendário — com no máximo 100 registros [limite de página], baldes por data corrida podem
 * ficar vazios/desiguais; por índice garante grupos não-vazios). Base de `bucketarContagem`/
 * `bucketarCumulativo`/`bucketarMedia` abaixo — a mesma técnica para as 3 sparklines derivadas de
 * listagem completa (Clientes, Pedidos, Ticket médio, Produção).
 */
function agruparEmBaldes<T>(itens: T[], dataDe: (item: T) => string, maxBaldes: number): T[][] {
  const ordenados = [...itens].sort((a, b) => dataDe(a).localeCompare(dataDe(b)));
  const nBaldes = Math.min(maxBaldes, ordenados.length);
  const tamanho = Math.ceil(ordenados.length / nBaldes);
  const baldes: T[][] = [];
  for (let i = 0; i < ordenados.length; i += tamanho) baldes.push(ordenados.slice(i, i + tamanho));
  return baldes;
}

/** Contagem de itens por balde cronológico (ex: OPS ativas criadas por período). */
function bucketarContagem<T>(itens: T[], dataDe: (item: T) => string, maxBaldes = 6): number[] | undefined {
  if (itens.length < 2) return undefined;
  return agruparEmBaldes(itens, dataDe, maxBaldes).map((balde) => balde.length);
}

/** Contagem acumulada por balde (ex: crescimento da base de clientes). */
export function bucketarCumulativo<T>(itens: T[], dataDe: (item: T) => string, maxBaldes = 6): number[] | undefined {
  const contagens = bucketarContagem(itens, dataDe, maxBaldes);
  if (!contagens) return undefined;
  let acumulado = 0;
  return contagens.map((c) => (acumulado += c));
}

/** Média de `valorDe` por balde cronológico (ex: ticket médio por período) — exportada porque `dashboard.page.ts` reaproveita a mesma lista de pedidos (`obterPedidosPorStatus`) filtrando só os pagos, então precisa montar essa série fora deste adapter. */
export function bucketarMedia<T>(itens: T[], dataDe: (item: T) => string, valorDe: (item: T) => number, maxBaldes = 6): number[] | undefined {
  if (itens.length < 2) return undefined;
  return agruparEmBaldes(itens, dataDe, maxBaldes).map((balde) => balde.reduce((soma, item) => soma + valorDe(item), 0) / balde.length);
}

export { bucketarContagem };
